import React, { useMemo } from "react";
import { useOutletContext, Link } from "react-router-dom";

/* --------------------- small utils --------------------- */
function safeStr(x) {
  return x == null ? "" : String(x).trim();
}
function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}
function fmtInt(x) {
  return num(x).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}
function fmtDT(v) {
  if (!v) return "—";
  try {
    const d = new Date(String(v));
    if (!Number.isNaN(d.getTime())) return d.toLocaleString("en-IN");
  } catch (_) {}
  return String(v);
}
function dt(x) {
  const s = safeStr(x);
  if (!s) return "—";
  return s.replace("T", " ").replace("+05:30", "").replace("Z", "").slice(0, 19);
}
function pct(part, total) {
  const t = num(total);
  if (t <= 0) return 0;
  return Math.round((num(part) * 1000) / t) / 10; // 1 decimal
}
function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}
function uniqBy(xs, keyFn) {
  const out = [];
  const seen = new Set();
  (xs || []).forEach((x) => {
    const k = keyFn(x);
    if (k == null) return;
    if (seen.has(k)) return;
    seen.add(k);
    out.push(x);
  });
  return out;
}

/* --------------------- UI atoms --------------------- */
function Badge({ tone = "slate", children }) {
  const TONE = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    red: "border-rose-200 bg-rose-50 text-rose-900",
    blue: "border-sky-200 bg-sky-50 text-sky-900",
    violet: "border-violet-200 bg-violet-50 text-violet-900",
  };
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-[12px] font-semibold",
        TONE[tone] || TONE.slate
      )}
    >
      {children}
    </span>
  );
}

function MetricCard({ title, value, hint, tone = "slate" }) {
  const TONE = {
    slate: { bg: "bg-white", ring: "border-slate-200", bar: "bg-slate-900/10", accent: "bg-slate-500" },
    green: { bg: "bg-emerald-50", ring: "border-emerald-200", bar: "bg-emerald-900/10", accent: "bg-emerald-500" },
    amber: { bg: "bg-amber-50", ring: "border-amber-200", bar: "bg-amber-900/10", accent: "bg-amber-500" },
    red: { bg: "bg-rose-50", ring: "border-rose-200", bar: "bg-rose-900/10", accent: "bg-rose-500" },
    blue: { bg: "bg-sky-50", ring: "border-sky-200", bar: "bg-sky-900/10", accent: "bg-sky-500" },
    violet: { bg: "bg-violet-50", ring: "border-violet-200", bar: "bg-violet-900/10", accent: "bg-violet-500" },
  };
  const t = TONE[tone] || TONE.slate;

  return (
    <div className={classNames("rounded-2xl border p-4", t.bg, t.ring)}>
      <div className="flex items-start gap-3">
        <div className={classNames("mt-1 h-9 w-1 rounded-full", t.accent)} />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">{title}</div>
          <div className="mt-2 text-3xl font-extrabold tabular-nums text-slate-900">{value}</div>
          {hint ? <div className="mt-1 text-xs text-slate-600">{hint}</div> : null}
          <div className={classNames("mt-3 h-1.5 w-full rounded-full", t.bar)} />
        </div>
      </div>
    </div>
  );
}

function Signal({ title, tone, children }) {
  const TONE = {
    good: "border-emerald-200 bg-emerald-50 text-emerald-900",
    warn: "border-amber-200 bg-amber-50 text-amber-900",
    bad: "border-rose-200 bg-rose-50 text-rose-900",
    info: "border-sky-200 bg-sky-50 text-sky-900",
  };
  return (
    <div className={classNames("rounded-2xl border p-4", TONE[tone] || TONE.info)}>
      <div className="text-xs font-semibold uppercase tracking-wider opacity-80">{title}</div>
      <div className="mt-2 text-sm font-semibold">{children}</div>
    </div>
  );
}

/* --------------------- Driver Overview --------------------- */
export default function SupportDriverOverviewTab() {
  const ctx = useOutletContext() || {};
  const driver = ctx.driver || null;

  // ✅ Owners list:
  // Preferred: ctx.owners (array) if your detail API returns it.
  // Fallback: driver.owners (array) or driver.owner single linkage.
  const owners = useMemo(() => {
    const raw =
      (Array.isArray(ctx?.owners) ? ctx.owners : null) ||
      (Array.isArray(driver?.owners) ? driver.owners : null) ||
      (driver?.owner ? [driver.owner] : null) ||
      (driver?.ownerId
        ? [
            {
              id: driver.ownerId,
              name: driver.ownerName,
              phoneNumber: driver.ownerPhone,
            },
          ]
        : []);
    const cleaned = (raw || []).map((o) => ({
      id: o?.id ?? o?.ownerId ?? null,
      name: safeStr(o?.name ?? o?.ownerName) || "—",
      phoneNumber: safeStr(o?.phoneNumber ?? o?.ownerPhone) || "",
      email: safeStr(o?.email) || "",
    }));
    return uniqBy(cleaned, (o) => o.id ?? `${o.name}|${o.phoneNumber}`); // stable
  }, [ctx?.owners, driver]);

  const ownerCount = owners.length;

  const coords = useMemo(() => {
    const lat = driver?.currentLatitude;
    const lng = driver?.currentLongitude;
    if (typeof lat === "number" && typeof lng === "number") return { lat, lng };
    return null;
  }, [driver]);

  const stats = useMemo(() => {
    const bookingTotal = num(driver?.bookingCount);
    const rideTotal = num(driver?.rideCount);

    const cancelled = num(driver?.cancelledBookingCount);
    const unfulfilled = num(driver?.unfulfilledBookingCount);

    // docs + online (kept for badges/signals)
    const docsComplete = !!driver?.documentsComplete;
    const docsVerified = !!driver?.documentsVerified;
    const online = driver?.online == null ? null : !!driver?.online;

    const lastAct = driver?.lastActivityAt || driver?.lastSeenAt || driver?.locationUpdatedAt;
    const activityTone = lastAct ? "good" : "warn";
    const activityText = lastAct ? `Last activity recorded at ${dt(lastAct)}.` : "No recent activity data available.";

    const docsTone = docsVerified ? "good" : docsComplete ? "warn" : "bad";
    const docsText = docsVerified
      ? "Documents verified. Driver is eligible for assignment."
      : docsComplete
        ? "Docs complete but not verified. Verify before enabling priority dispatch."
        : "Docs incomplete. Driver may face restrictions.";

    const onlineTone = online === true ? "good" : online === false ? "info" : "warn";
    const onlineText =
      online === true ? "Driver is currently online." : online === false ? "Driver is offline." : "Online status unavailable.";

    const locTone = coords ? "good" : "warn";
    const locText = coords
      ? `Live coordinates available (${coords.lat}, ${coords.lng}).`
      : "No live coordinates. Ensure app permissions and location ping.";

    // Simple outcome message (optional)
    const unfulfilledShare = bookingTotal > 0 ? pct(unfulfilled, bookingTotal) : 0;
    const cancelledShare = bookingTotal > 0 ? pct(cancelled, bookingTotal) : 0;
    const outcomeTone =
      bookingTotal <= 0 ? "info" : unfulfilled >= 3 ? "bad" : cancelled >= 3 ? "warn" : "good";
    const outcomeText =
      bookingTotal <= 0
        ? "No booking history for this driver yet."
        : `Cancelled ${cancelledShare}%, Unfulfilled ${unfulfilledShare}% (of total bookings).`;

    return {
      bookingTotal,
      rideTotal,
      cancelled,
      unfulfilled,

      docsComplete,
      docsVerified,
      online,
      lastAct,

      activityTone,
      activityText,
      docsTone,
      docsText,
      onlineTone,
      onlineText,
      locTone,
      locText,

      outcomeTone,
      outcomeText,
    };
  }, [driver, coords]);

  if (!driver) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="text-sm font-semibold text-slate-600">No data</div>
      </div>
    );
  }

  const name = driver?.name || "Driver";
  const phone = safeStr(driver?.phoneNumber) || "—";
  const email = safeStr(driver?.email) || "—";
  const license = safeStr(driver?.licenseNumber) || "—";
  const trackedReg = safeStr(driver?.trackedVehicleRegNo) || "—";
  const driverId = driver?.id ?? "—";

  return (
    <div className="min-w-0 space-y-4">
      {/* Overview Header */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-lg font-extrabold text-slate-900">{name}</div>
            <Badge tone="slate">ID: {driverId}</Badge>

            <Badge tone={stats.docsVerified ? "green" : stats.docsComplete ? "amber" : "red"}>
              DOCS: {stats.docsVerified ? "VERIFIED" : stats.docsComplete ? "COMPLETE" : "INCOMPLETE"}
            </Badge>

            {stats.online == null ? (
              <Badge tone="amber">ONLINE: —</Badge>
            ) : (
              <Badge tone={stats.online ? "green" : "slate"}>ONLINE: {stats.online ? "YES" : "NO"}</Badge>
            )}

            {stats.lastAct ? <Badge tone="blue">LAST ACTIVITY: {dt(stats.lastAct)}</Badge> : <Badge tone="amber">LAST ACTIVITY: —</Badge>}

            <Badge tone={ownerCount > 0 ? "violet" : "amber"}>OWNERS LINKED: {fmtInt(ownerCount)}</Badge>
          </div>

          <div className="mt-2 text-sm text-slate-600">
            <span className="font-semibold text-slate-900">{phone}</span>
            <span className="mx-2 text-slate-300">•</span>
            <span className="truncate">{email}</span>
            {license !== "—" ? (
              <>
                <span className="mx-2 text-slate-300">•</span>
                <span className="truncate">
                  License: <span className="font-semibold text-slate-900">{license}</span>
                </span>
              </>
            ) : null}
          </div>

          {/* tiny row: tracked + location */}
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge tone="slate">Tracked Vehicle: {trackedReg}</Badge>
            <Badge tone={coords ? "green" : "amber"}>Location: {coords ? "AVAILABLE" : "—"}</Badge>
            {driver?.trackedVehicleUntil ? <Badge tone="slate">Tracked Until: {fmtDT(driver.trackedVehicleUntil)}</Badge> : null}
          </div>
        </div>
      </div>

      {/* ✅ ONLY 4 MAIN CELLS */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard title="Total Rides" value={fmtInt(stats.rideTotal)} hint={stats.rideTotal > 0 ? "All rides for this driver." : "—"} tone="violet" />
        <MetricCard title="Total Bookings" value={fmtInt(stats.bookingTotal)} hint={stats.bookingTotal > 0 ? "All bookings for this driver." : "—"} tone="blue" />
        <MetricCard
          title="Total Unfulfilled"
          value={fmtInt(stats.unfulfilled)}
          hint={stats.bookingTotal > 0 ? `${pct(stats.unfulfilled, stats.bookingTotal)}% of bookings` : "—"}
          tone="amber"
        />
        <MetricCard
          title="Total Cancelled"
          value={fmtInt(stats.cancelled)}
          hint={stats.bookingTotal > 0 ? `${pct(stats.cancelled, stats.bookingTotal)}% of bookings` : "—"}
          tone="red"
        />
      </div>

     

      

      {/* Driver details */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Driver details</div>
        </div>

        <div className="grid grid-cols-1 gap-4 px-5 py-5 md:grid-cols-2">
          <div>
            <div className="text-[12px] font-semibold text-slate-500">License</div>
            <div className="mt-1 text-[14px] tabular-nums text-slate-900">{license}</div>
          </div>

          <div>
            <div className="text-[12px] font-semibold text-slate-500">Tracked vehicle</div>
            <div className="mt-1 text-[14px] tabular-nums text-slate-900">
              {trackedReg}
              {driver?.trackedVehicleUntil ? <span className="text-slate-400"> • until {fmtDT(driver.trackedVehicleUntil)}</span> : null}
            </div>
          </div>

          <div>
            <div className="text-[12px] font-semibold text-slate-500">Last seen</div>
            <div className="mt-1 text-[14px] text-slate-900">{fmtDT(driver?.lastSeenAt)}</div>
          </div>

          <div>
            <div className="text-[12px] font-semibold text-slate-500">Location updated</div>
            <div className="mt-1 text-[14px] text-slate-900">{fmtDT(driver?.locationUpdatedAt)}</div>
          </div>

          <div className="md:col-span-2">
            <div className="text-[12px] font-semibold text-slate-500">Current location</div>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <div className="text-[14px] tabular-nums text-slate-900">{coords ? `${coords.lat}, ${coords.lng}` : "—"}</div>
              {coords ? (
                <a
                  className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                  href={`https://www.google.com/maps?q=${encodeURIComponent(`${coords.lat},${coords.lng}`)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open map
                </a>
              ) : null}
            </div>
          </div>

          <div>
            <div className="text-[12px] font-semibold text-slate-500">Created</div>
            <div className="mt-1 text-[14px] text-slate-900">{fmtDT(driver?.createdAt)}</div>
          </div>

          <div>
            <div className="text-[12px] font-semibold text-slate-500">Updated</div>
            <div className="mt-1 text-[14px] text-slate-900">{fmtDT(driver?.updatedAt)}</div>
          </div>

          <div>
            <div className="text-[12px] font-semibold text-slate-500">Last booking</div>
            <div className="mt-1 text-[14px] tabular-nums text-slate-900">
              {driver?.lastBookingId ? `${driver.lastBookingId} • ${driver?.lastBookingStatus || "—"}` : "—"}
            </div>
          </div>

          <div>
            <div className="text-[12px] font-semibold text-slate-500">Last ride</div>
            <div className="mt-1 text-[14px] tabular-nums text-slate-900">
              {driver?.lastRideId ? `${driver.lastRideId} • ${driver?.lastRideStatus || "—"}` : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Owners list section at bottom */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <div className="text-[12px] font-bold uppercase tracking-wider text-slate-500">Owners linked</div>
            <div className="mt-1 text-sm font-semibold text-slate-700">
              {ownerCount > 0 ? `${ownerCount} owner(s)` : "No owners linked"}
            </div>
          </div>
          <Badge tone={ownerCount > 0 ? "violet" : "amber"}>COUNT: {fmtInt(ownerCount)}</Badge>
        </div>

        {ownerCount <= 0 ? (
          <div className="px-5 py-5 text-sm text-slate-600">No owner linkage present for this driver.</div>
        ) : (
          <div className="px-5 py-5">
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Owner</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Phone</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Email</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {owners.map((o) => {
                    const id = o.id;
                    return (
                      <tr key={id ?? `${o.name}|${o.phoneNumber}`}>
                        <td className="px-4 py-3">
                          <div className="text-sm font-semibold text-slate-900">
                            {o.name} {id ? <span className="text-slate-400">• ID {id}</span> : null}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">{o.phoneNumber || "—"}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{o.email || "—"}</td>
                        <td className="px-4 py-3 text-right">
                          {id ? (
                            <Link
                              to={`/admin/support/owners/${id}`}
                              className="inline-flex h-8 items-center rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-900 hover:bg-slate-50"
                            >
                              Open owner
                            </Link>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="mt-4 grid gap-2 sm:hidden">
              {owners.map((o) => {
                const id = o.id;
                return (
                  <div key={id ?? `${o.name}|${o.phoneNumber}`} className="rounded-xl border border-slate-200 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-extrabold text-slate-900">{o.name}</div>
                        <div className="mt-1 text-xs text-slate-600">
                          {o.phoneNumber || "—"}
                          {id ? <span className="text-slate-300"> • </span> : null}
                          {id ? <span className="font-semibold text-slate-900">ID {id}</span> : null}
                        </div>
                      </div>
                      {id ? (
                        <Link
                          to={`/admin/support/owners/${id}`}
                          className="inline-flex h-8 shrink-0 items-center rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-900 hover:bg-slate-50"
                        >
                          Open
                        </Link>
                      ) : null}
                    </div>
                    <div className="mt-2 text-xs text-slate-600">{o.email || "—"}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
